/**
 * Enhanced Taxonomy Service - V1.5.0 Backend-Only Implementation
 *
 * This service exclusively uses the Backend API for all taxonomy operations.
 * Features:
 * - Initialization on app start for performance
 * - Manual refresh capability via UI
 * - MFA/HFN conversion for backend communication standardization
 * - Preserved component metadata sync framework compatibility
 * - Non-breaking migration with graceful error handling
 *
 * Updated: August 8, 2025 - Removed toggle, always use Backend API
 */

import { logger } from '../utils/logger';
import { TaxonomyItem } from '../types/taxonomy.types';
// Import the original frontend service for fallback
import { taxonomyService as frontendTaxonomyService } from './simpleTaxonomyService';
// Import the dynamic taxonomy service for proper async protocol implementation
import { dynamicTaxonomyService } from './dynamicTaxonomyService';
// Import the centralized backend URL configuration
import { getBackendUrl as getCentralizedBackendUrl } from '../utils/environment.config';
// Import Asset type for taxonomy validation
import { Asset } from '../types/asset.types';
// Import debug configuration for controlled logging
import { debugLog } from '../utils/debugConfig';
import api from '../http/axiosClient';

// Track if we've already logged the environment check during this session
let environmentCheckLogged = false;

// Track initialization state
let taxonomyInitialized = false;
let initializationPromise: Promise<void> | null = null;

/**
 * Initialize taxonomy from backend on app start
 * This loads the taxonomy data once and caches it for performance
 */
export async function initializeTaxonomy(): Promise<void> {
  if (taxonomyInitialized) {
    console.log('✅ [TAXONOMY SERVICE] Already initialized');
    return;
  }

  if (initializationPromise) {
    console.log('⏳ [TAXONOMY SERVICE] Initialization in progress, waiting...');
    return initializationPromise;
  }

  initializationPromise = performInitialization();
  await initializationPromise;
  initializationPromise = null;
}

async function performInitialization(): Promise<void> {
  try {
    console.log(
      '🚀 [TAXONOMY SERVICE] Initializing taxonomy from Backend API...'
    );
    logger.info('Initializing taxonomy from Backend API');

    // Preload all layers
    const layers = await getLayers();
    console.log(`✅ [TAXONOMY SERVICE] Loaded ${layers.length} layers`);

    // Preload categories for common layers
    const commonLayers = ['G', 'S', 'L', 'M', 'W'];
    for (const layer of commonLayers) {
      if (layers.includes(layer)) {
        await getCategories(layer);
        console.log(
          `✅ [TAXONOMY SERVICE] Preloaded categories for layer ${layer}`
        );
      }
    }

    taxonomyInitialized = true;
    console.log('✅ [TAXONOMY SERVICE] Taxonomy initialization complete');
    logger.info('Taxonomy initialization complete');

    // Only log environment check once per session
    if (!environmentCheckLogged) {
      console.log('🔧 [TAXONOMY SERVICE] Using Backend API exclusively');
      logger.debug('[TAXONOMY SERVICE] Using Backend API exclusively');
      environmentCheckLogged = true;
    }
  } catch (error) {
    console.error(
      '🚨 [TAXONOMY SERVICE] Failed to initialize taxonomy:',
      error
    );
    logger.error('Failed to initialize taxonomy:', error);
    // Don't throw to allow app to continue
  }
}

/**
 * Refresh taxonomy data from backend
 * Called when user clicks the refresh button in the UI
 */
export async function refreshTaxonomy(): Promise<void> {
  try {
    console.log(
      '🔄 [TAXONOMY SERVICE] Refreshing taxonomy from Backend API...'
    );
    logger.info('Refreshing taxonomy from Backend API');

    // Clear all caches
    cache.clear();

    // Reset initialization flag
    taxonomyInitialized = false;

    // Re-initialize
    await initializeTaxonomy();

    console.log('✅ [TAXONOMY SERVICE] Taxonomy refresh complete');
    logger.info('Taxonomy refresh complete');
  } catch (error) {
    console.error('🚨 [TAXONOMY SERVICE] Failed to refresh taxonomy:', error);
    logger.error('Failed to refresh taxonomy:', error);
    throw error;
  }
}

/**
 * Environment-aware backend URL selection for when backend mode is enabled
 * Uses the centralized environment configuration to ensure consistency
 */
function getBackendUrl(): string {
  // Use the centralized backend URL configuration
  // This ensures consistency with the main application's environment detection
  return getCentralizedBackendUrl();
}

/**
 * Enhanced fetch wrapper with error handling and logging
 */
async function fetchWithErrorHandling(
  url: string,
  options?: RequestInit
): Promise<any> {
  try {
    debugLog('TAXONOMY', `🚀 [ENHANCED TAXONOMY] API Request: ${url}`);
    logger.debug(`API Request: ${url}`);
    const startTime = Date.now();

    const response = await api.request({
      url,
      method: options?.method as
        | 'GET'
        | 'POST'
        | 'PUT'
        | 'DELETE'
        | 'PATCH'
        | undefined,
      headers: options?.headers as Record<string, string> | undefined,
      data:
        typeof options?.body === 'string'
          ? JSON.parse(options.body)
          : options?.body,
      validateStatus: status => status >= 200 && status < 300,
    });

    const responseTime = Date.now() - startTime;
    debugLog(
      'TAXONOMY',
      `📡 [ENHANCED TAXONOMY] API Response: ${url} (${responseTime}ms) - ${response.status}`
    );
    logger.debug(
      `API Response: ${url} (${responseTime}ms) - ${response.status}`
    );

    const data = response.data;
    debugLog(
      'TAXONOMY',
      `✅ [ENHANCED TAXONOMY] API Data received from ${url}:`,
      JSON.stringify(data).substring(0, 200) + '...'
    );
    logger.debug(
      `API Data received: ${JSON.stringify(data).substring(0, 200)}...`
    );

    return data;
  } catch (error) {
    console.error(`🚨 [ENHANCED TAXONOMY] API Error for ${url}:`, error);
    logger.error(`API Error for ${url}:`, error);
    throw error;
  }
}

/**
 * Simple in-memory cache for API responses
 * Backend is fast (<100ms) but caching reduces unnecessary calls
 */
class TaxonomyCache {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  set(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  get(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }

    logger.debug(`Cache hit: ${key}`);
    return cached.data;
  }

  clear(): void {
    this.cache.clear();
    logger.debug('Taxonomy cache cleared');
  }
}

const cache = new TaxonomyCache();

/**
 * V1.5.0 Taxonomy Migration Support Class
 * Handles MFA/HFN conversion and fallback taxonomy loading
 */
class TaxonomyV150Support {
  private initialized = false;

  constructor() {
    this.initializeV150Support();
  }

  private async initializeV150Support() {
    if (this.initialized) return;

    try {
      // Check for taxonomy version changes and invalidate cache if needed
      this.checkAndInvalidateStaleCache();

      this.initialized = true;
      console.log('✅ [TAXONOMY V1.5.0] Support initialized successfully');
    } catch (error) {
      console.warn('⚠️ [TAXONOMY V1.5.0] Failed to initialize support:', error);
      logger.warn('Failed to initialize V1.5.0 taxonomy support:', error);
    }
  }

  private async checkAndInvalidateStaleCache() {
    // Cache invalidation now uses time-based expiration only
    // No version checking - rely on cache TTL
    try {
      // Check if cache has expired based on time
      const cacheTimestamp = localStorage.getItem('nna-taxonomy-cache-timestamp');
      const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

      if (cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp, 10);
        if (cacheAge > CACHE_TTL) {
          console.log('🔄 [TAXONOMY CACHE] Cache expired, invalidating...');
          localStorage.setItem('nna-taxonomy-cache-timestamp', Date.now().toString());
          console.log('✅ [TAXONOMY CACHE] Cache invalidated successfully');
        }
      } else {
        // First time - set timestamp
        localStorage.setItem('nna-taxonomy-cache-timestamp', Date.now().toString());
      }
    } catch (error) {
      console.warn(
        '⚠️ [TAXONOMY CACHE] Failed to check/invalidate cache:',
        error
      );
    }
  }

  // MFA/HFN conversion methods - now use backend API
  convertHFNToMFA(hfn: string): string {
    // For now, return as-is since backend API handles conversion
    // This method is kept for backward compatibility
    if (!hfn) return '';
    return hfn;
  }

  convertMFAToHFN(mfa: string): string {
    // For now, return as-is since backend API handles conversion
    // This method is kept for backward compatibility
    if (!mfa) return '';
    return mfa;
  }

  // Enhanced category fetching with backend API only
  async getCategoriesForLayerV150(layer: string): Promise<any> {
    await this.initializeV150Support();

    // Always use backend API
    try {
      const baseUrl = getBackendUrl();
      const url = `${baseUrl}/taxonomy/layers/${layer}/categories`;
      const response = await fetchWithErrorHandling(url);

      console.log(
        `✅ [TAXONOMY V1.5.0] Backend API categories for layer ${layer}:`,
        response
      );
      
      // Backend returns array of categories, wrap in expected format
      if (Array.isArray(response)) {
        return {
          layer,
          categories: response,
          count: response.length,
          source: 'backend',
        };
      }
      
      return response;
    } catch (error) {
      console.error('🚨 [TAXONOMY V1.5.0] Backend API failed:', error);
      logger.error(`Backend API failed for layer ${layer}:`, error);
      throw error; // Don't fallback, let the error propagate
    }
  }

  // Enhanced subcategory fetching
  async getSubcategoriesForCategoryV150(
    layer: string,
    category: string
  ): Promise<any> {
    await this.initializeV150Support();

    // Use category code directly (backend expects code, not HFN)
    const categoryCode = category;

    // Always use backend API
    try {
      const baseUrl = getBackendUrl();
      const url = `${baseUrl}/taxonomy/layers/${layer}/categories/${categoryCode}/subcategories`;
      const response = await fetchWithErrorHandling(url);

      console.log(
        `✅ [TAXONOMY V1.5.0] Backend API subcategories for ${layer}/${categoryCode}:`,
        response
      );
      
      // Backend returns array of subcategories, wrap in expected format
      if (Array.isArray(response)) {
        return {
          layer,
          category: categoryCode,
          subcategories: response,
          count: response.length,
          source: 'backend',
        };
      }
      
      return response;
    } catch (error) {
      console.error(
        `🚨 [TAXONOMY V1.5.0] Backend API failed for ${layer}/${categoryCode}:`,
        error
      );
      logger.error(`Backend API failed for ${layer}/${categoryCode}:`, error);

      // For 404 errors, return empty subcategories to prevent crashes
      if (
        (error as Error)?.message?.includes('404') ||
        (error as Error)?.message?.includes('HTTP 404')
      ) {
        console.warn(
          `⚠️ [TAXONOMY V1.5.0] Category not found: ${layer}/${categoryCode}`
        );
        return {
          layer,
          category: categoryCode,
          subcategories: [],
          count: 0,
          source: 'empty',
          error: 'Category not found in backend',
        };
      }

      // For other errors, throw
      throw error;
    }
  }

  // Enhanced validation with V1.5.0 taxonomy
  async validateAssetTaxonomyV150(asset: any): Promise<{
    valid: boolean;
    error?: string;
    suggestedAlternatives?: string[];
  }> {
    try {
      await this.initializeV150Support();

      const layer = asset.layer;
      const category = asset.category || asset.categoryCode;
      const subcategory = asset.subcategory || asset.subcategoryCode;

      // Get categories for layer to validate
      const categoriesResponse = await this.getCategoriesForLayerV150(layer);
      const validCategories = categoriesResponse.categories.map(
        (cat: any) => cat.code
      );

      // Use category code directly (backend uses codes)
      const mfaCategory = category;

      if (!validCategories.includes(mfaCategory)) {
        return {
          valid: false,
          error: `Category "${category}" (MFA: ${mfaCategory}) not found in layer ${layer}`,
          suggestedAlternatives: validCategories.slice(0, 3),
        };
      }

      // Validate subcategory if provided
      if (subcategory) {
        const subcategoriesResponse =
          await this.getSubcategoriesForCategoryV150(layer, mfaCategory);
        const validSubcategories = subcategoriesResponse.subcategories.map(
          (sub: any) => sub.code
        );
        // Use subcategory code directly (backend uses codes)
        const mfaSubcategory = subcategory;

        if (!validSubcategories.includes(mfaSubcategory)) {
          return {
            valid: false,
            error: `Subcategory "${subcategory}" (MFA: ${mfaSubcategory}) not found in ${layer}/${mfaCategory}`,
            suggestedAlternatives: validSubcategories.slice(0, 3),
          };
        }
      }

      return { valid: true };
    } catch (error) {
      console.warn(
        '⚠️ [TAXONOMY V1.5.0] Validation error (non-blocking):',
        error
      );
      logger.warn('V1.5.0 validation error (non-blocking):', error);
      return { valid: true }; // Non-blocking validation
    }
  }

  // Version management methods
  setUseBackendAPI(useAPI: boolean) {
    localStorage.setItem('nna-use-backend-taxonomy', useAPI.toString());
    console.log(
      `🔧 [TAXONOMY V1.5.0] Mode: ${
        useAPI ? 'Backend API' : 'Fallback taxonomy'
      }`
    );
  }

  isUsingBackendAPI(): boolean {
    return true; // Always true now
  }

  async getTaxonomyVersion(): Promise<string> {
    // Return static version - no API call
    return 'v1.5.2-dynamic';
  }
}

// Initialize V1.5.0 support
const v150Support = new TaxonomyV150Support();

/**
 * Gets all available layers - always uses Backend API
 * Uses localStorage cache for performance optimization
 */
export async function getLayers(): Promise<string[]> {
  // Use localStorage cache with 24-hour TTL
  const baseUrl = getBackendUrl();
  const url = `${baseUrl}/taxonomy/layers`;

  const data = await fetchWithErrorHandling(url);

  // Backend returns object like {"G": "Songs", "S": "Stars", ...}
  // Convert to array of layer codes
  const layers = Object.keys(data);

  logger.debug(`Retrieved ${layers.length} layers from backend:`, layers);

  return layers;
}

/**
 * Gets categories for a specific layer - always uses Backend API
 * Uses localStorage cache for performance optimization
 */
export async function getCategories(layer: string): Promise<TaxonomyItem[]> {
  // Use localStorage cache with 24-hour TTL
  const baseUrl = getBackendUrl();
  const url = `${baseUrl}/taxonomy/layers/${layer}/categories`;

  const data = await fetchWithErrorHandling(url);

  // Backend returns: {"layer": "S", "categories": [{"code": "POP", "name": "Pop", "numericCode": "001"}], "count": 16}
  const categories: TaxonomyItem[] = data.map((cat: any) => ({
    id: cat.id,
    code: cat.code,
    name: cat.name,
    numericCode: cat.numericCode,
  }));

  logger.debug(
    `Retrieved ${categories.length} categories for layer ${layer} from backend`
  );

  return categories;
}

/**
 * Gets subcategories for a specific layer and category - always uses Backend API
 * Uses localStorage cache for performance optimization
 */
export async function getSubcategories(
  layer: string,
  categoryCode: string
): Promise<TaxonomyItem[]> {
  // Use localStorage cache with 24-hour TTL
  const baseUrl = getBackendUrl();
  const url = `${baseUrl}/taxonomy/layers/${layer}/categories/${categoryCode}/subcategories`;

  const data = await fetchWithErrorHandling(url);

  // Backend returns: {"layer": "S", "category": "POP", "subcategories": [{"code": "POP.BAS", "name": "Base", "numericCode": "001"}], "count": 16}
  const subcategories: TaxonomyItem[] = data.map(
    (sub: any) => {
      // Extract subcategory code from full code (e.g., "POP.BAS" → "BAS")
      const subCode = sub.code.includes('.')
        ? sub.code.split('.')[1]
        : sub.code;

      return {
        id: sub.id,
        code: subCode,
        name: sub.name,
        numericCode: sub.numericCode,
      };
    }
  );

  logger.debug(
    `Retrieved ${subcategories.length} subcategories for ${layer}.${categoryCode}`
  );

  return subcategories;
}

/**
 * Converts a Human-Friendly Name (HFN) to a Machine-Friendly Address (MFA) - always uses Backend API
 */
export async function convertHFNtoMFA(hfn: string): Promise<string> {
  if (!hfn) return '';

  logger.debug(
    `Using backend taxonomy service for HFN->MFA conversion: ${hfn}`
  );

  try {
    const baseUrl = getBackendUrl();
    const url = `${baseUrl}/taxonomy/convert/hfn-to-mfa`;

    const response = await fetchWithErrorHandling(url, {
      method: 'POST',
      body: JSON.stringify({ hfn }),
    });

    // Backend returns: {"hfn": "S.POP.BAS.001", "mfa": "2.001.001.001", "success": true}
    if (response.success && response.mfa) {
      logger.debug(`Converted HFN to MFA: ${hfn} → ${response.mfa}`);
      return response.mfa;
    } else {
      logger.error('Backend conversion failed:', response);
      // Fallback to dynamic taxonomy service
      try {
        return await dynamicTaxonomyService.convertHFNtoMFA(hfn);
      } catch (dynamicError) {
        logger.error('Dynamic taxonomy service also failed:', dynamicError);
        return frontendTaxonomyService.convertHFNtoMFA(hfn);
      }
    }
  } catch (error) {
    logger.error(`Failed to convert HFN to MFA via backend: ${hfn}`, error);
    // Fallback to dynamic taxonomy service on error
    logger.debug('Falling back to dynamic taxonomy service');
    try {
      return await dynamicTaxonomyService.convertHFNtoMFA(hfn);
    } catch (dynamicError) {
      logger.error('Dynamic taxonomy service also failed:', dynamicError);
      return frontendTaxonomyService.convertHFNtoMFA(hfn);
    }
  }
}

/**
 * Converts a Machine-Friendly Address (MFA) to a Human-Friendly Name (HFN) - always uses Backend API
 */
export async function convertMFAtoHFN(mfa: string): Promise<string> {
  if (!mfa) return '';

  logger.debug(
    `Using backend taxonomy service for MFA->HFN conversion: ${mfa}`
  );

  try {
    const baseUrl = getBackendUrl();
    const url = `${baseUrl}/taxonomy/convert/mfa-to-hfn`;

    const response = await fetchWithErrorHandling(url, {
      method: 'POST',
      body: JSON.stringify({ mfa }),
    });

    // Backend returns: {"mfa": "2.001.001.001", "hfn": "S.POP.BAS.001", "success": true}
    if (response.success && response.hfn) {
      logger.debug(`Converted MFA to HFN: ${mfa} → ${response.hfn}`);
      return response.hfn;
    } else {
      logger.error('Backend conversion failed:', response);
      // Fallback to dynamic taxonomy service
      try {
        return await dynamicTaxonomyService.convertMFAtoHFN(mfa);
      } catch (dynamicError) {
        logger.error('Dynamic taxonomy service also failed:', dynamicError);
        return frontendTaxonomyService.convertMFAtoHFN(mfa);
      }
    }
  } catch (error) {
    logger.error(`Failed to convert MFA to HFN via backend: ${mfa}`, error);
    // Fallback to dynamic taxonomy service on error
    logger.debug('Falling back to dynamic taxonomy service');
    try {
      return await dynamicTaxonomyService.convertMFAtoHFN(mfa);
    } catch (dynamicError) {
      logger.error('Dynamic taxonomy service also failed:', dynamicError);
      return frontendTaxonomyService.convertMFAtoHFN(mfa);
    }
  }
}


/**
 * Refreshes taxonomy data when version changes are detected
 */
export async function refreshTaxonomyData(): Promise<void> {
  try {
    console.log('🔄 [TAXONOMY REFRESH] Refreshing taxonomy data...');
    logger.info('Refreshing taxonomy data due to version change');

    // Clear the cache to force fresh data
    clearTaxonomyCache();

    // Pre-load critical taxonomy data
    await getLayers();
    console.log('✅ [TAXONOMY REFRESH] Taxonomy data refreshed successfully');
    logger.info('Taxonomy data refreshed successfully');
  } catch (error) {
    console.error(
      '🚨 [TAXONOMY REFRESH] Error refreshing taxonomy data:',
      error
    );
    logger.error('Error refreshing taxonomy data:', error);
    throw error;
  }
}

/**
 * TAXONOMY EVOLUTION PROTOCOL - PHASE 1: CORE VALIDATION
 * Implementation of frontend specification for taxonomy protocol compliance
 */

export interface TaxonomyValidationResult {
  valid: boolean;
  error?: string;
  deprecatedCombination?: string;
  suggestedAlternatives?: string[];
}

/**
 * Validates an asset against current taxonomy
 * This is the core validation function for the taxonomy evolution protocol
 */
export async function validateAssetTaxonomy(
  asset: Asset
): Promise<TaxonomyValidationResult> {
  try {
    // Extract taxonomy fields (Asset type has both categoryCode/subcategoryCode and category/subcategory)
    const layer = asset.layer;
    const category = asset.category || asset.categoryCode;
    const subcategory = asset.subcategory || asset.subcategoryCode;

    // Validate required fields
    if (!layer || !category || !subcategory) {
      return {
        valid: false,
        error: `Asset "${asset.name}" missing required taxonomy fields (layer, category, subcategory)`,
        deprecatedCombination: `${layer || 'MISSING'}.${
          category || 'MISSING'
        }.${subcategory || 'MISSING'}`,
      };
    }

    // Get current taxonomy for validation
    const subcategories = await getSubcategories(layer, category);
    const validSubcategories = subcategories.map(sub => sub.code);

    // Check if subcategory exists in current taxonomy
    const isValid = validSubcategories.includes(subcategory);

    if (!isValid) {
      return {
        valid: false,
        error: `Asset "${asset.name}" uses deprecated taxonomy combination (${layer}.${category}.${subcategory}) that is no longer supported.`,
        deprecatedCombination: `${layer}.${category}.${subcategory}`,
        suggestedAlternatives: validSubcategories.map(
          sub => `${layer}.${category}.${sub}`
        ),
      };
    }

    return { valid: true };
  } catch (error) {
    logger.error('Error validating asset taxonomy:', error);

    const layer = asset.layer;
    const category = asset.category || asset.categoryCode;
    const subcategory = asset.subcategory || asset.subcategoryCode;

    // CRITICAL FIX: On validation error, assume valid to avoid filtering out assets
    // This prevents network errors or backend issues from breaking asset functionality
    console.warn(
      `⚠️ [TAXONOMY VALIDATION] Error validating asset "${asset.name}", assuming valid to preserve functionality`
    );

    return {
      valid: true, // CHANGED: Assume valid on error to avoid breaking functionality
      error:
        'Failed to validate asset against current taxonomy. Asset allowed but may use deprecated taxonomy.',
      deprecatedCombination: `${layer}.${category}.${subcategory}`,
    };
  }
}

/**
 * Filters assets to exclude those with deprecated taxonomy combinations
 * Used to prevent selection of invalid assets in composite workflows
 */
export async function filterValidAssets(assets: Asset[]): Promise<Asset[]> {
  try {
    const validationPromises = assets.map(async asset => {
      const validation = await validateAssetTaxonomy(asset);
      return { asset, validation };
    });

    const validationResults = await Promise.all(validationPromises);
    const validAssets = validationResults
      .filter(result => result.validation.valid)
      .map(result => result.asset);

    const deprecatedCount = assets.length - validAssets.length;
    if (deprecatedCount > 0) {
      logger.warn(
        `Filtered out ${deprecatedCount} assets with deprecated taxonomy combinations`
      );
    }

    return validAssets;
  } catch (error) {
    logger.error('Error filtering assets:', error);

    // On error, return original assets to avoid breaking functionality
    return assets;
  }
}

/**
 * Gets a list of deprecated assets from the current asset set
 * Used for reporting and cleanup purposes
 */
export async function getDeprecatedAssets(
  assets: Asset[]
): Promise<{ asset: Asset; reason: string }[]> {
  try {
    const deprecatedAssets: { asset: Asset; reason: string }[] = [];

    for (const asset of assets) {
      const validation = await validateAssetTaxonomy(asset);
      if (!validation.valid) {
        deprecatedAssets.push({
          asset,
          reason: validation.error || 'Unknown validation error',
        });
      }
    }

    return deprecatedAssets;
  } catch (error) {
    logger.error('Error detecting deprecated assets:', error);
    return [];
  }
}

/**
 * Clears the taxonomy cache
 * Useful for refreshing data when backend is updated
 */
export function clearTaxonomyCache(): void {
  cache.clear();
  logger.info(
    'Taxonomy cache cleared - fresh data will be fetched on next request'
  );
}

/**
 * Inspection utility for debugging taxonomy structure (backward compatibility)
 */
export async function inspectTaxonomyStructure(
  layer: string,
  categoryCode: string
): Promise<Record<string, any>> {
  try {
    logger.debug(`Inspecting taxonomy structure: ${layer}.${categoryCode}`);

    // Get categories for the layer
    const categories = await getCategories(layer);
    const categoryExists = categories.some(cat => cat.code === categoryCode);

    if (!categoryExists) {
      return { exists: false, reason: 'category_not_found' };
    }

    // Get subcategories for the layer/category
    const subcategories = await getSubcategories(layer, categoryCode);

    return {
      exists: true,
      categoryName:
        categories.find(cat => cat.code === categoryCode)?.name || categoryCode,
      subcategories: subcategories.map(sub => ({
        id: sub.numericCode,
        code: sub.code,
        name: sub.name,
      })),
    };
  } catch (error) {
    logger.error('Error inspecting taxonomy structure:', error);
    return { exists: false, reason: 'error', error: String(error) };
  }
}

/**
 * Debug utility for inspecting API responses
 */
export async function debugTaxonomyAPI(
  layer?: string,
  categoryCode?: string
): Promise<void> {
  logger.debug('=== Taxonomy API Debug ===');

  try {
    // Test layers
    const layers = await getLayers();
    logger.debug('Layers:', layers);

    if (layer) {
      // Test categories for specific layer
      const categories = await getCategories(layer);
      logger.debug(`Categories for ${layer}:`, categories);

      if (categoryCode && categories.length > 0) {
        // Test subcategories for specific layer/category
        const subcategories = await getSubcategories(layer, categoryCode);
        logger.debug(
          `Subcategories for ${layer}.${categoryCode}:`,
          subcategories
        );
      }
    }

    logger.debug('=== End Taxonomy API Debug ===');
  } catch (error) {
    logger.error('Debug failed:', error);
  }
}

/**
 * Get taxonomy path for display (Layer > Category > Subcategory)
 * This method provides the human-readable path string for breadcrumb display
 * Updated to use dynamic backend API for layer names (v1.5.2)
 */
export async function getTaxonomyPath(
  layerCode?: string,
  categoryCode?: string,
  subcategoryCode?: string
): Promise<string> {
  if (!layerCode) return '';

  try {
    // Get layer name dynamically from backend API
    let layerName = layerCode;
    try {
      const baseUrl = getBackendUrl();
      const layersUrl = `${baseUrl}/taxonomy/layers`;
      const layersData = await fetchWithErrorHandling(layersUrl);

      // Backend returns object like {"G": "Songs", "S": "Stars", ...}
      layerName = layersData[layerCode] || layerCode;
    } catch (error) {
      console.warn(
        `Failed to get layer name for ${layerCode} from backend, using code as fallback`
      );
      // Fallback to layerCode if backend call fails
      layerName = layerCode;
    }

    let path = layerName;

    // Only continue if category code is provided
    if (!categoryCode) {
      return path;
    }

    try {
      // Get categories from backend to find the name
      const categories = await getCategories(layerCode);
      const category = categories.find(cat => cat.code === categoryCode);
      const categoryName = category?.name || categoryCode;

      path += ' > ' + categoryName;

      // Only continue if subcategory code is provided
      if (!subcategoryCode) {
        return path;
      }

      // Get subcategories from backend to find the name
      // UPDATED v1.5.2: Use dynamic category code resolution instead of hardcoded mappings
      let categoryCodeForAPI = categoryCode;

      // If category code has underscores or unusual format, try to resolve via backend first
      if (
        categoryCode &&
        (categoryCode.includes('_') || categoryCode.length > 3)
      ) {
        try {
          // Check if this category exists in the backend categories
          const categories = await getCategories(layerCode);
          const matchingCategory = categories.find(
            cat =>
              cat.code === categoryCode ||
              cat.name === categoryCode ||
              cat.code.toLowerCase() === categoryCode.toLowerCase()
          );

          if (matchingCategory) {
            categoryCodeForAPI = matchingCategory.code;
            console.log(
              `✅ [TAXONOMY SERVICE v1.5.2] Resolved category "${categoryCode}" to "${categoryCodeForAPI}" via backend`
            );
          } else {
            // Fallback to truncated code only if no match found
            categoryCodeForAPI = categoryCode.substring(0, 3).toUpperCase();
            console.log(
              `⚠️ [TAXONOMY SERVICE v1.5.2] No backend match for "${categoryCode}", using fallback "${categoryCodeForAPI}"`
            );
          }
        } catch (error) {
          console.warn(
            `Failed to resolve category "${categoryCode}" via backend, using fallback`
          );
          categoryCodeForAPI = categoryCode.substring(0, 3).toUpperCase();
        }
      }

      let subcategoryName = subcategoryCode;
      try {
        const subcategories = await getSubcategories(
          layerCode,
          categoryCodeForAPI
        );
        const subcategory = subcategories.find(
          sub =>
            sub.code === subcategoryCode ||
            sub.code === `${categoryCodeForAPI}.${subcategoryCode}`
        );
        subcategoryName = subcategory?.name || subcategoryCode;
      } catch (subcategoryError) {
        console.warn(
          `Failed to get subcategories for ${layerCode}.${categoryCodeForAPI}, using fallback names`
        );
        // Use the original codes if backend call fails
        subcategoryName = subcategoryCode;
      }

      path += ' > ' + subcategoryName;
    } catch (error) {
      logger.warn(
        'Failed to get taxonomy names from backend, using codes:',
        error
      );
      // Fallback to codes if backend calls fail
      if (categoryCode) path += ' > ' + categoryCode;
      if (subcategoryCode) path += ' > ' + subcategoryCode;
    }

    return path;
  } catch (error) {
    logger.error('Error getting taxonomy path:', error);
    // Dynamic fallback - try to get at least layer name from backend
    const parts = [];

    try {
      const baseUrl = getBackendUrl();
      const layersUrl = `${baseUrl}/taxonomy/layers`;
      const layersData = await fetchWithErrorHandling(layersUrl);
      const layerName = layersData[layerCode] || layerCode;
      parts.push(layerName);
    } catch {
      // Ultimate fallback to code
      parts.push(layerCode);
    }

    if (categoryCode) parts.push(categoryCode);
    if (subcategoryCode) parts.push(subcategoryCode);
    return parts.join(' > ');
  }
}

/**
 * V1.5.0 Enhanced MFA/HFN Conversion Functions
 * These functions use the V1.5.0 support class for standardized backend communication
 */
export function convertHFNToMFA_V150(hfn: string): string {
  return v150Support.convertHFNToMFA(hfn);
}

export function convertMFAToHFN_V150(mfa: string): string {
  return v150Support.convertMFAToHFN(mfa);
}

export async function getCategoriesV150(layer: string): Promise<any> {
  return await v150Support.getCategoriesForLayerV150(layer);
}

export async function getSubcategoriesV150(
  layer: string,
  category: string
): Promise<any> {
  return await v150Support.getSubcategoriesForCategoryV150(layer, category);
}

export async function validateAssetTaxonomyV150(asset: any): Promise<{
  valid: boolean;
  error?: string;
  suggestedAlternatives?: string[];
}> {
  return await v150Support.validateAssetTaxonomyV150(asset);
}

export function setTaxonomyModeV150(useBackendAPI: boolean): void {
  v150Support.setUseBackendAPI(useBackendAPI);
}

export function isTaxonomyBackendEnabledV150(): boolean {
  return v150Support.isUsingBackendAPI();
}

export async function getTaxonomyVersionV150(): Promise<string> {
  return await v150Support.getTaxonomyVersion();
}

export function getFallbackTaxonomyV150(): any {
  // Fallback taxonomy removed - always use backend API
  console.warn('getFallbackTaxonomyV150() is deprecated - backend API is always used');
  return null;
}

// Export all functions as a service object for backward compatibility
export const enhancedTaxonomyService = {
  getLayers,
  getCategories,
  getSubcategories,
  convertHFNtoMFA,
  convertMFAtoHFN,
  clearTaxonomyCache,
  debugTaxonomyAPI,
  inspectTaxonomyStructure,
  getTaxonomyPath,
  // Taxonomy Evolution Protocol functions
  validateAssetTaxonomy,
  filterValidAssets,
  getDeprecatedAssets,
  // V1.5.0 Enhanced Functions
  convertHFNToMFA_V150,
  convertMFAToHFN_V150,
  getCategoriesV150,
  getSubcategoriesV150,
  validateAssetTaxonomyV150,
  setTaxonomyModeV150,
  isTaxonomyBackendEnabledV150,
  getTaxonomyVersionV150,
  getFallbackTaxonomyV150,
};

export default enhancedTaxonomyService;
